import React, { useMemo } from "react";
import { FieldValues, useController } from "react-hook-form";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { useOpportunityWorkflow } from "../../../lib/hooks/api/opportunities/useOpportunityWorkflow";
import { Select, SelectProps } from "../../form_components/Inputs";
import { renderStageOption } from "./stageSelectUtils";
import { processTransitions, StageOption } from "./utils";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options" | "onChange"> & {
  fillbackground?: boolean;
  onChange?: (value: string, option?: StageOption) => void;
  preventFormValueChange?: boolean;
};

export function OpportunityStageDropdownSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { data, isLoading } = useOpportunityWorkflow();

  const { field } = useController(props);

  const options = useMemo(() => (data ? processTransitions(data.transitions) : []), [data]);

  let backgroundColor = undefined;

  const optionCache: Record<string, StageOption> = useMemo(
    () =>
      options
        .flatMap((group) => group.options)
        .reduce((acc, option) => ({ ...acc, [option.value]: option }), {}),
    [options],
  );

  if (props.fillbackground && props.variant === "borderless") {
    const selectedOption = optionCache[field.value];
    backgroundColor = selectedOption?.color ?? "transparent";
  }

  return (
    <Select
      {...props}
      onChange={(value) => {
        if (props.onChange) {
          props.onChange(value, optionCache[value]);
        }
      }}
      loading={isLoading}
      options={options}
      labelRender={(props) => renderStageOption(props.label, props.value, options)}
      optionRender={(oriOption) => renderStageOption(oriOption.label, oriOption.value, options)}
      style={{ backgroundColor }}
      preventFormValueChange={props.preventFormValueChange}
      suffixIcon={<UIcon icon="chevronDown" color="sysTextDefault" />}
    />
  );
}
